import React from 'react';
import { useState } from "react";
import { useSnackbar } from "notistack";
import { useNavigate, Navigate } from "react-router-dom";
import { isAuthenticated } from "../../infrastructure/utils/auth";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Fade,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  Person as PersonOutlineIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import api, { setAuthToken } from "../../infrastructure/config/api";

// Styled components
const StyledContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
  padding: "16px",
  boxSizing: "border-box",
  overflow: "auto",
  WebkitOverflowScrolling: "touch",
  [theme.breakpoints.down("sm")]: {
    padding: "12px",
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: "100%",
  maxWidth: "400px",
  padding: "24px",
  margin: "auto",
  borderRadius: "12px",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
  background: "rgba(26, 32, 53, 0.95)",
  backdropFilter: "blur(10px)",
  color: "#fff",
  position: "relative",
  display: "flex",
  flexDirection: "column",
  maxHeight: "calc(100vh - 32px)",
  overflowY: "auto",
  WebkitOverflowScrolling: "touch",
  "&::-webkit-scrollbar": {
    display: "none",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "20px 16px",
    maxHeight: "calc(100vh - 24px)",
  },
}));

const StyledTitle = styled(Typography)({
  fontWeight: 700,
  background: "linear-gradient(90deg, #00c6ff 0%, #0072ff 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  marginBottom: 8,
  fontSize: "1.75rem",
});

const StyledSubtitle = styled(Typography)({
  marginBottom: 32,
  color: "rgba(255, 255, 255, 0.7)",
  fontSize: "1rem",
  fontWeight: 400,
  lineHeight: 1.5,
});

const StyledTextField = styled(TextField)({
  marginBottom: 12,
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    "&.MuiInputBase-sizeSmall": {
      padding: "8px 12px",
    },
    "& fieldset": {
      borderColor: "rgba(255, 255, 255, 0.1)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255, 255, 255, 0.3)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#0072ff",
    },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255, 255, 255, 0.7)",
    fontSize: "0.875rem",
    "&.Mui-focused": {
      color: "#0072ff",
    },
  },
  "& .MuiInputBase-input": {
    color: "#fff",
    padding: "10px 12px",
    fontSize: "0.875rem",
  },
  "& .MuiInputAdornment-root": {
    marginRight: 8,
    "& .MuiSvgIcon-root": {
      fontSize: "1.1rem",
    },
  },
});

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: "24px",
  padding: "12px 0",
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "1rem",
  background: "linear-gradient(90deg, #00c6ff 0%, #0072ff 100%)",
  boxShadow: "0 4px 15px rgba(0, 114, 255, 0.3)",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 6px 20px rgba(0, 114, 255, 0.4)",
    background: "linear-gradient(90deg, #00c6ff 0%, #0072ff 100%)",
  },
  "&:active": {
    transform: "translateY(0)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "14px 0",
    fontSize: "0.9375rem",
  },
}));

const StyledFooter = styled(Typography)({
  marginTop: 32,
  textAlign: "center",
  color: "rgba(255, 255, 255, 0.5)",
  fontSize: "0.8rem",
  borderTop: "1px solid rgba(255, 255, 255, 0.05)",
  paddingTop: 16,
});

const Login = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const { username, password } = formData;

  // Check authentication status
  const isAuthenticatedUser = isAuthenticated();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await api.post("/api/auth/login", {
        username: username.trim(),
        password: password,
      });

      const { token, user } = response.data;

      if (!token || !user) {
        throw new Error("Invalid response from server");
      }

      console.log('✅ Login successful!');
      console.log('  - User:', user);
      console.log('  - User ID:', user?.id);
      console.log('  - Username:', user?.username);

      // Clear tất cả localStorage trước (để đảm bảo không còn data cũ)
      localStorage.clear();
      console.log('🧹 Cleared all localStorage');

      // Save token and user data mới
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));
      console.log('💾 Saved new token and user data to localStorage');

      // Set default auth header for future requests
      setAuthToken(token);
      console.log('🔑 Set auth token in axios headers');

      // Show success message
      enqueueSnackbar("Đăng nhập thành công!", { variant: "success" });

      // Navigate to dashboard WITHOUT reload (preserve localStorage)
      console.log('🔄 Navigating to dashboard...');
      console.log('📊 Final check - Token in localStorage:', !!localStorage.getItem('token'));
      console.log('📊 Final check - User in localStorage:', !!localStorage.getItem('user'));

      // Use navigate instead of window.location to avoid reload
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error("Login error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Đăng nhập thất bại. Vui lòng thử lại.";
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Redirect if already logged in
  if (isAuthenticatedUser) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <StyledContainer>
      <Fade in={true} timeout={500}>
        <StyledPaper elevation={0}>
          <Box textAlign="center" mb={{ xs: 3, sm: 4 }}>
            <Box
              sx={{
                width: "72px",
                height: "72px",
                margin: "0 auto 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#1a2038",
                borderRadius: "12px",
                boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  color: "#ffffff",
                  fontWeight: "bold",
                  textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                }}
              >
                TB
              </Typography>
            </Box>
            <StyledTitle
              variant="h4"
              sx={{
                fontSize: { xs: "1.5rem", sm: "1.75rem" },
                mb: 1,
              }}
            >
              Chào mừng trở lại
            </StyledTitle>
            <StyledSubtitle
              variant="body1"
              sx={{
                fontSize: { xs: "0.9375rem", sm: "1rem" },
                color: "rgba(255, 255, 255, 0.7)",
              }}
            >
              Vui lòng đăng nhập để tiếp tục
            </StyledSubtitle>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{
              flex: "1 1 auto",
              display: "flex",
              flexDirection: "column",
              "& .MuiFormHelperText-root": {
                mx: 0,
                mt: 0.5,
                fontSize: "0.75rem",
                position: "absolute",
                bottom: "-20px",
                left: 0,
              },
              "& > * + *": {
                mt: 2,
              },
            }}
          >
            <Box sx={{ position: "relative", mb: 3 }}>
              <StyledTextField
                fullWidth
                id="username"
                label="Tên đăng nhập"
                name="username"
                autoComplete="username"
                autoFocus
                value={username}
                onChange={handleChange}
                disabled={loading}
                size="small"
                margin="none"
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                    >
                      <PersonOutlineIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "48px",
                  },
                }}
              />
            </Box>
            <Box sx={{ position: "relative", mb: 3 }}>
              <StyledTextField
                fullWidth
                name="password"
                label="Mật khẩu"
                type={showPassword ? "text" : "password"}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={handleChange}
                disabled={loading}
                size="small"
                margin="none"
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                    >
                      <LockIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                        sx={{
                          color: "rgba(255, 255, 255, 0.7)",
                          mr: -1,
                        }}
                      >
                        {showPassword ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "48px",
                  },
                }}
              />
            </Box>

            <Box sx={{ mt: "auto", pt: 2 }}>
              <StyledButton
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <LoginIcon />
                  )
                }
                sx={{
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 600,
                }}
              >
                {loading ? "Đang xử lý..." : "Đăng nhập"}
              </StyledButton>
            </Box>
          </Box>

          <StyledFooter
            sx={{
              mt: 4,
              pt: 2,
              fontSize: { xs: "0.75rem", sm: "0.8rem" },
              borderTop: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            {new Date().getFullYear()} Trading Bot. All rights reserved.
          </StyledFooter>
        </StyledPaper>
      </Fade>
    </StyledContainer>
  );
};

export default Login;
